import React from 'react';
import styles from './Insights.module.scss';
import {
  Button,
  ButtonProps,
  Heading,
  Icon,
  IconList,
  Message,
  Text,
  Badge,
  MessageControls,
} from 'components/core';
import { Toast, ToastButton, createToast, dismissToast } from 'components/core';
import { ID } from 'util/localTypes';

type Props = {
  organizationId: ID;
};

const BaseInsightsButton = ({
  organizationId,
  children,
  type,
  variant,
}: Props & { children: React.ReactNode } & ButtonProps) => {
  const [isLoading, setIsLoading] = React.useState(false);

  const openInNewTab = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    // If popups are blocked, do nothing in the current tab
  };

  const onClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    const triggerElement = event.currentTarget;

    dismissToast();
    setIsLoading(true);
    try {
      const response = await fetch(
        `/organizations/${organizationId}/insights`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        }
      );

      type InsightsResponse = {
        url?: string;
        error?: string;
      };
      let json: InsightsResponse | undefined;
      try {
        json = (await response.json()) as InsightsResponse;
      } catch {
        json = undefined;
      }

      if (!response.ok) {
        const message =
          json?.error || 'Oops! Something went wrong opening insights';
        throw new Error(message);
      }

      const url = json?.url?.trim();
      if (!url) throw new Error('Empty URL returned');
      openInNewTab(url);
    } catch (e: any) {
      createToast(
        <Toast type="danger" focusOnAppear restoreFocusTo={triggerElement}>
          {e?.message || 'Oops! Something went wrong opening insights'}
          <ToastButton to={window.location.href}>Reload</ToastButton>
        </Toast>
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      type={type}
      variant={variant}
      isLoading={isLoading}
      onClick={onClick}
      htmlType="button"
    >
      {children}
    </Button>
  );
};
export const InsightsBanner = ({ organizationId }: Props) => {
  return (
    <Message
      variant="subtle"
      icon={IconList.solid.faChartColumn}
      dismissible={false}
      className={styles.message}
    >
      <Heading type="h5" component="h2" className={styles.heading}>
        Research insights dashboard
        <Badge variant="solid" type="primary" className={styles.badge}>
          New
        </Badge>
      </Heading>
      <Text>
        See how Covidence is being used across your organization, how activity
        is driving real research outcomes, and get insights you can share with
        leadership.
      </Text>
      <MessageControls>
        <BaseInsightsButton
          organizationId={organizationId}
          type="brand"
          variant="solid"
        >
          View research insights
        </BaseInsightsButton>
      </MessageControls>
    </Message>
  );
};

export const InsightsButton = ({ organizationId }: Props) => {
  return (
    <BaseInsightsButton
      organizationId={organizationId}
      type="neutral"
      variant="outline"
    >
      Insights
      <Icon
        icon={IconList.light.faArrowUpRightFromSquare}
        aria-label="- opens in new window"
        aria-hidden={false}
      />
    </BaseInsightsButton>
  );
};
